import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { api } from './api';
import { date, decimalInput, money } from './ui';

/** A delivered order on this side not yet reconciled or on a statement, of either kind of unit. */
export interface Candidate {
  id: string;
  partnerId: string;
  partnerName: string;
  /** A unit not on the platform: this enterprise issues and the statement takes effect at once. */
  local: boolean;
  number: string;
  sku_name: string;
  line_count: number;
  delivered_at: string | null;
  order_amount: string;
  /** The suggested amount to bill. */
  amount: string;
}

interface Props {
  side: 'SELL' | 'BUY';
  candidates: Candidate[];
  partnerId?: string;
  onClose: () => void;
  onDone: (statementId: string, local: boolean) => void;
}

const monthOf = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;

/** Orders delivered up to the end of the period are in by default; later ones can be added. */
const deliveredBy = (row: any, period: string) => {
  const [y, m] = period.split('-').map(Number);
  return !row.delivered_at || new Date(row.delivered_at) < new Date(y, m, 1);
};

/**
 * Statements are usually drawn up after a month closes, so default to last month — unless
 * nothing was delivered by then, in which case this month's deliveries are what is to bill.
 */
function defaultPeriod(unbilled: any[]) {
  const now = new Date();
  const last = monthOf(new Date(now.getFullYear(), now.getMonth() - 1, 1));
  return unbilled.some((row) => deliveredBy(row, last)) ? last : monthOf(now);
}

export default function StatementDialog(p: Props) {
  const selling = p.side === 'SELL';
  const unbilled = p.candidates;
  const partners = useMemo(() => {
    const map = new Map<string, { id: string; name: string; local: boolean }>();
    for (const row of unbilled)
      map.set(row.partnerId, { id: row.partnerId, name: row.partnerName, local: row.local });
    return [...map.values()];
  }, [unbilled]);
  const [partnerId, setPartnerId] = useState(p.partnerId ?? partners[0]?.id ?? '');
  const partner = partners.find((x) => x.id === partnerId);
  const [period, setPeriod] = useState(() => defaultPeriod(unbilled));
  const orders = unbilled.filter((row) => row.partnerId === partnerId);
  const [amounts, setAmounts] = useState<Record<string, string>>(() =>
    Object.fromEntries(unbilled.map((r) => [r.id, r.amount])),
  );
  const [picked, setPicked] = useState<Set<string>>(
    () => new Set(unbilled.filter((r) => deliveredBy(r, defaultPeriod(unbilled))).map((r) => r.id)),
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [key] = useState(() => crypto.randomUUID());

  const chosen = orders.filter((row) => picked.has(row.id));
  const total = chosen.reduce((sum, row) => sum + Number(amounts[row.id] || 0), 0);
  const toggle = (id: string) =>
    setPicked((previous) => {
      const next = new Set(previous);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const changePeriod = (value: string) => {
    setPeriod(value);
    if (/^\d{4}-\d{2}$/.test(value))
      setPicked(new Set(unbilled.filter((r) => deliveredBy(r, value)).map((r) => r.id)));
  };

  async function submit() {
    if (busy) return;
    if (!chosen.length) return setError('请至少选择一张订单');
    if (chosen.some((row) => amounts[row.id] === '')) return setError('请填写每张订单的对账金额');
    setBusy(true);
    setError('');
    try {
      const result = partner?.local
        ? await api<{ id: string }>('/local-statements', {
            counterpartyId: partnerId,
            kind: selling ? 'SALE' : 'PURCHASE',
            period,
            note,
            lines: chosen.map((row) => ({ orderId: row.id, amount: amounts[row.id] })),
            key,
          })
        : await api<{ id: string }>('/statements', {
            partnerId,
            side: p.side,
            period,
            note,
            lines: chosen.map((row) => ({ tradeId: row.id, amount: amounts[row.id] })),
            key,
          });
      p.onDone(result.id, !!partner?.local);
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Dialog open fullWidth maxWidth="md" onClose={() => !busy && p.onClose()}>
      <DialogTitle sx={{ pb: 1 }}>
        生成{selling ? '应收' : '应付'}对账单
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          {partner?.local
            ? '本企业登记的单位：发出即生效，各订单金额一并锁定；之后可发给对方确认。'
            : '发出即代表本企业确认，对方确认后各订单金额一并锁定，不再受理退货。'}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {partners.length === 0 ? (
          <Typography color="text.secondary" sx={{ py: 4, textAlign: 'center', fontSize: 13 }}>
            没有可对账的订单：订单需全部交付完成，且尚未单独对账或加入其他对账单。
          </Typography>
        ) : (
          <>
            <Box sx={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 2, pt: 1, mb: 2 }}>
              <TextField
                select
                label={selling ? '客户' : '供应商'}
                value={partnerId}
                disabled={busy}
                onChange={(e) => setPartnerId(e.target.value)}
              >
                {partners.map((row) => (
                  <MenuItem key={row.id} value={row.id}>
                    {row.name}
                    {row.local && (
                      <Box component="span" sx={{ color: 'text.secondary', fontSize: 12, ml: 1 }}>
                        本企业登记
                      </Box>
                    )}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                type="month"
                label="对账月份"
                value={period}
                disabled={busy}
                slotProps={{ inputLabel: { shrink: true } }}
                onChange={(e) => changePeriod(e.target.value)}
              />
            </Box>
            <Box sx={{ border: '1px solid #e7edef', borderRadius: 1, overflowX: 'auto' }}>
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ '& th': { whiteSpace: 'nowrap' } }}>
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={orders.length > 0 && chosen.length === orders.length}
                        indeterminate={chosen.length > 0 && chosen.length < orders.length}
                        onChange={(e) =>
                          setPicked((previous) => {
                            const next = new Set(previous);
                            for (const row of orders)
                              if (e.target.checked) next.add(row.id);
                              else next.delete(row.id);
                            return next;
                          })
                        }
                      />
                    </TableCell>
                    <TableCell>订单</TableCell>
                    <TableCell>
                      {partner?.local ? `完成${selling ? '出库' : '入库'}` : '完成收货'}
                    </TableCell>
                    <TableCell align="right">订单金额</TableCell>
                    <TableCell align="right" sx={{ width: 150 }}>
                      对账金额
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {orders.map((row) => {
                    const differs =
                      Number(amounts[row.id] || 0).toFixed(2) !==
                      Number(row.order_amount).toFixed(2);
                    return (
                      <TableRow key={row.id} selected={picked.has(row.id)}>
                        <TableCell padding="checkbox">
                          <Checkbox
                            size="small"
                            checked={picked.has(row.id)}
                            disabled={busy}
                            onChange={() => toggle(row.id)}
                          />
                        </TableCell>
                        <TableCell>
                          <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                            {row.sku_name}
                            {row.line_count > 1 && ` 等 ${row.line_count} 种`}
                          </Typography>
                          <Typography color="text.secondary" sx={{ fontSize: 11.5 }}>
                            {row.number}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                          {date(row.delivered_at)}
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', fontSize: 13 }}>
                          {money(row.order_amount)}
                        </TableCell>
                        <TableCell align="right">
                          <TextField
                            size="small"
                            value={amounts[row.id] ?? ''}
                            disabled={busy || !picked.has(row.id)}
                            onChange={(e) =>
                              setAmounts({ ...amounts, [row.id]: decimalInput(e.target.value, 2) })
                            }
                            helperText={differs ? '按实收 / 退货调整' : undefined}
                            slotProps={{
                              input: {
                                startAdornment: <InputAdornment position="start">¥</InputAdornment>,
                              },
                              htmlInput: { style: { textAlign: 'right' } },
                              formHelperText: { sx: { mx: 0, textAlign: 'right' } },
                            }}
                          />
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </Box>
            <Typography color="text.secondary" sx={{ fontSize: 12, mt: 1 }}>
              {partner?.local
                ? `对账金额默认按实际${selling ? '出库数量、扣除客户签收短少' : '入库数量'}计算，可按约定调整。`
                : '对账金额默认按合格实收减去已退回数量计算，可按双方约定调整。'}
            </Typography>
            <TextField
              label="备注（选填）"
              placeholder="例如：含 10 月运费补差"
              value={note}
              disabled={busy}
              slotProps={{ inputLabel: { shrink: true } }}
              onChange={(e) => setNote(e.target.value)}
              sx={{ mt: 2.5 }}
              fullWidth
            />
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider', gap: 1 }}>
        <Typography sx={{ flex: 1, fontSize: 13 }} color="text.secondary">
          已选 {chosen.length} 单 · 合计{' '}
          <Box component="span" sx={{ fontSize: 18, fontWeight: 700, color: 'text.primary' }}>
            {money(total)}
          </Box>
        </Typography>
        <Button disabled={busy} onClick={p.onClose}>
          取消
        </Button>
        <Button variant="contained" disabled={busy || !chosen.length} onClick={submit}>
          {busy ? '正在保存…' : partner?.local ? '生成对账单' : '发出对账单'}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
